import { createReducer, on } from '@ngrx/store';

import { OptimizationHistoryItem } from '@core/models/optimization-history.model';
import { OptimizationResult } from '@core/models/optimization.model';
import * as AuthActions from '@core/store/auth/auth.actions';
import * as PlanActions from '@core/store/plan/plan.actions';

import { OptimizationHistoryActions } from './optimization-history.actions';
import {
  OptimizationEvent,
  OptimizationHistoryState,
  initialOptimizationHistoryState,
  isUnseenMatchingCreation
} from './optimization-history.state';

function patchRow(
  row: OptimizationHistoryItem,
  event: OptimizationResult
): OptimizationHistoryItem {
  const isCancelable = event.isCancelable ?? false;
  return {
    ...row,
    status: event.status,
    isCancelable,
    // canCancel comes from the server's permission check; an event can only withdraw it.
    canCancel: row.canCancel && isCancelable,
    // The event names no plan; reloadForMissingNames$ fetches the name.
    outputPlan:
      event.outputPlanId && event.outputPlanId !== row.outputPlan?.id
        ? { id: event.outputPlanId, name: null, isDeleted: false }
        : row.outputPlan,
    // The event carries no username, so merge instead of replacing to keep the row's.
    canceledBy: event.canceledBy
      ? { ...row.canceledBy, ...event.canceledBy }
      : row.canceledBy
  };
}

function applyUpdate(
  state: OptimizationHistoryState,
  result: OptimizationResult
): OptimizationHistoryState {
  if (!result.jobId) return state;
  const index = state.items.findIndex((item) => item.id === result.jobId);
  // An event lacks the names a row needs, so a new job can only be flagged or reloaded.
  if (index === -1) {
    return state.pageOpen && isUnseenMatchingCreation(state, result) && state.query.page > 1
      ? { ...state, hasUnseenJobs: true }
      : state;
  }
  const items = [...state.items];
  items[index] = patchRow(items[index], result);
  return { ...state, items };
}

function applyDeletion(
  state: OptimizationHistoryState,
  jobId: string
): OptimizationHistoryState {
  const items = state.items.filter((item) => item.id !== jobId);
  if (items.length === state.items.length) return state;
  return { ...state, items, total: Math.max(0, state.total - 1) };
}

function applyEvent(
  state: OptimizationHistoryState,
  event: OptimizationEvent
): OptimizationHistoryState {
  return event.type === PlanActions.optimizationDeleted.type
    ? applyDeletion(state, event.jobId)
    : applyUpdate(state, event.result);
}

export const optimizationHistoryReducer = createReducer(
  initialOptimizationHistoryState,

  on(
    OptimizationHistoryActions.load,
    (state, { query }): OptimizationHistoryState => ({
      ...state,
      query,
      loading: true,
      eventsDuringLoad: []
    })
  ),
  on(
    OptimizationHistoryActions.loadSuccess,
    (state, { page }): OptimizationHistoryState =>
      state.eventsDuringLoad.reduce(applyEvent, {
        ...state,
        items: page.result,
        total: page.total,
        loading: false,
        eventsDuringLoad: [],
        hasUnseenJobs: false
      })
  ),
  on(
    OptimizationHistoryActions.pageEntered,
    (state): OptimizationHistoryState => ({ ...state, pageOpen: true })
  ),
  on(
    OptimizationHistoryActions.pageLeft,
    (state): OptimizationHistoryState => ({ ...state, pageOpen: false })
  ),
  on(
    OptimizationHistoryActions.loadFailure,
    (state): OptimizationHistoryState => ({
      ...state,
      loading: false,
      eventsDuringLoad: []
    })
  ),

  on(PlanActions.optimizationUpdated, PlanActions.optimizationDeleted, (state, event) =>
    applyEvent(
      state.loading
        ? { ...state, eventsDuringLoad: [...state.eventsDuringLoad, event] }
        : state,
      event
    )
  ),

  // Rows carry other users' names, so they must not outlive the session.
  on(AuthActions.logout, (): OptimizationHistoryState => initialOptimizationHistoryState)
);
